import * as React from 'react';
import { useMemo } from 'react';
import DataTableHeader from 'components/common/table/datatable/DataTableHeader';
import NewDataTableBody from 'components/common/table/datatable/NewDataTableBody';
import DataTableLayout from 'components/common/table/datatable/DataTableLayout';
import {
  GetInvoiceDetailItem,
  GetInvoiceDetailItemType,
  GetInvoiceDetailPricingAmount,
} from 'types/plansales/invoice/invoiceDetail';
import { TextStyle } from 'components/common/table/TableStyle';
import {
  renderInvoiceDetailItemDescriptionColumn,
  renderInvoiceDetailItemTypeColumn,
} from 'modules/billing/invoiceDetail';
import { TableCell } from 'types/common/table';
import InvoiceTableBottomSummary from '../../billing/detail/invoice/InvoiceTableBottomSummary';

const hiddenPriceTypes: GetInvoiceDetailItemType[] = [
  GetInvoiceDetailItemType.CARRY_OVER_BALANCE,
  GetInvoiceDetailItemType.DOWNGRADE_SUBSCRIPTION,
];

interface Props {
  invoiceItems: GetInvoiceDetailItem[];
  planName: string;
  paymentAmount: GetInvoiceDetailPricingAmount;
  isRefunded: boolean;
}

/**
 * Invoice Detail 하단의 Item List Table
 * @param invoiceItems
 * @param planName
 * @param paymentAmount
 * @param isRefunded
 * @constructor
 */
const InvoiceDetailItemList = ({ invoiceItems, planName, paymentAmount, isRefunded }: Props) => {
  const columns: TableCell<GetInvoiceDetailItem>[] = useMemo(
    () => [
      {
        align: 'left',
        name: 'Item',
        key: 'item',
        render: (rowData: GetInvoiceDetailItem) => (
          <TextStyle fontWeight={'600'} textAlign={'left'}>
            {renderInvoiceDetailItemTypeColumn(rowData.type, planName)}
          </TextStyle>
        ),
      },
      {
        align: 'left',
        name: 'Description',
        key: 'description',
        render: (rowData: GetInvoiceDetailItem) => (
          <TextStyle textAlign={'left'}>
            {renderInvoiceDetailItemDescriptionColumn(rowData, planName)}
          </TextStyle>
        ),
      },
      {
        align: 'center',
        name: 'Qty',
        key: 'qty',
        render: (rowData: GetInvoiceDetailItem) => {
          // 결제 취소 시 노출하지 않는 것으로 결정.
          return (
            <TextStyle textAlign={'center'}>
              {hiddenPriceTypes.includes(rowData.type) ? '' : rowData.quantity}
            </TextStyle>
          );
        },
      },
      {
        align: 'right',
        name: 'Unit Price',
        key: 'unitPrice',
        render: (rowData: GetInvoiceDetailItem) => (
          // TODO: 추후에 통화 표기 받아서 달러/유로 붙여줘야함
          <TextStyle textAlign={'right'}>
            {rowData.unitPrice ? rowData.unitPrice.toFixed(2) : ''}
          </TextStyle>
        ),
      },
      {
        align: 'right',
        name: 'Amount',
        key: 'amount',
        width: '10%',
        render: (rowData: GetInvoiceDetailItem) => (
          <TextStyle textAlign={'right'}>{rowData.amount}</TextStyle>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <DataTableLayout tableTitle={'Item List'}>
        <DataTableHeader header={columns} />
        <NewDataTableBody<GetInvoiceDetailItem> dataList={invoiceItems} columns={columns} />
      </DataTableLayout>
      <InvoiceTableBottomSummary
        isRefunded={isRefunded}
        subTotalAmount={paymentAmount.subTotal}
        totalAmount={paymentAmount.total}
        taxAmount={paymentAmount.tax}
      />
    </>
  );
};

export default InvoiceDetailItemList;
